import NProgress from 'nprogress';
import { useMemo, useCallback } from 'react';
import { isEqualPath } from 'minimal-shared/utils';
import { useNavigate } from 'react-router-dom';

// ----------------------------------------------------------------------

/**
 * React Router adapter that preserves the previous Next.js `useRouter` shape
 * (push / replace / back / forward / refresh / prefetch) plus NProgress.
 */

export type AppRouter = {
  push: (href: string, options?: { replace?: boolean }) => void;
  replace: (href: string) => void;
  back: () => void;
  forward: () => void;
  refresh: () => void;
  prefetch: (href?: string) => void;
};

export function useRouter(): AppRouter {
  const navigate = useNavigate();

  const startProgress = useCallback((href: string) => {
    if (typeof window !== 'undefined' && !isEqualPath(href, window.location.href, { deep: false })) {
      NProgress.start();
    }
  }, []);

  const push = useCallback(
    (href: string, options?: { replace?: boolean }) => {
      startProgress(href);
      navigate(href, { replace: options?.replace });
    },
    [navigate, startProgress]
  );

  const replace = useCallback(
    (href: string) => {
      startProgress(href);
      navigate(href, { replace: true });
    },
    [navigate, startProgress]
  );

  return useMemo(
    () => ({
      push,
      replace,
      back: () => navigate(-1),
      forward: () => navigate(1),
      refresh: () => navigate(0),
      prefetch: () => {},
    }),
    [push, replace, navigate]
  );
}
